import { useMutation, useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { t } from 'i18next';
import { httpService } from '../../../utils/apiService';

const useFetchChargingStations = ({ operatorId, offSet, limit, search }) => {
  return useQuery({
    queryKey: ['listStations', operatorId, offSet, search],
    queryFn: async () => {
      const params = queryString.stringify({
        offset: offSet,
        limit,
        search,
      });

      const response = await httpService.post(`location/list?${params}`, {
        operatorId: operatorId,
      });

      return response.data.data;
    },
    keepPreviousData: true,
    enabled: !!operatorId,
    refetchOnWindowFocus: false,
  });
};

const useFetchSingleLocation = ({ id, operatorId }) => {
  return useQuery({
    queryKey: ['GET_SINGLE_CHARGER', operatorId, id],
    queryFn: async () => {
      try {
        const response = await httpService.post(
          `location/single-location/${id}`,
          { operatorId }
        );

        return response.data.data;
      } catch (error) {
        // Transform the error to provide better error messages
        const errorMessage = error?.response?.data?.message || error?.message || t('location:locationNotFound');
        const transformedError = new Error(errorMessage);
        transformedError.response = error.response;
        throw transformedError;
      }
    },
    refetchInterval: 20000,
    enabled: !!id && !!operatorId,
    refetchOnWindowFocus: false,
  });
};

const useStopCharging = (options = {}) => {
  return useMutation({
    mutationKey: ['DELETE_CHARGER'],
    mutationFn: async (data) => {
      const response = await httpService.post(
        `charger-history/authority-stop`,
        data
      );

      return response.data.data;
    },
    ...options,
  });
};

const useGetChargersByLocation = ({ operatorId, locationId, offset, limit = 10, search = '' }) => {
  return useQuery({
    queryKey: ['GET_Charger_location', locationId, offset, limit, search],
    queryFn: async () => {
      const response = await httpService.post(
        `charger/location-based?limit=${limit}&offset=${offset}${search ? `&search=${search}` : ''}`,
        {
          operatorId,
          locationId,
        }
      );

      return response.data.data;
    },
    refetchInterval: 20000,
    enabled: !!operatorId && !!locationId,
    refetchOnWindowFocus: false,
  });
};

const useGetSingleCharger = (params = {}, options = {}) => {
  const { id, operatorId } = params;
  return useQuery({
    queryKey: ['SELECTED_CHARGERS', id],
    queryFn: async () => {
      try {
        const response = await httpService.post(`charger/single-charger/${id}`, {
          operatorId,
        });
        return response.data.data;
      } catch (error) {
        // Transform the error to provide better error messages
        const errorMessage = error?.response?.data?.message || error?.message || t('chargerPage:chargerNotFound');
        const transformedError = new Error(errorMessage);
        transformedError.response = error.response;
        throw transformedError;
      }
    },
    enabled: !!id,
    ...options,
  });
};

const useCreateLocation = (params = {}, options = {}) => {
  const { id } = params;
  const apiStatement = id
    ? (payload) => httpService.post(`/location/update/${id}`, payload)
    : (payload) => httpService.post(`/location/create`, payload);

  return useMutation({
    mutationKey: ['CREATE_STATION'],
    mutationFn: async (payload) => {
      const response = await apiStatement(payload);
      return response.data.data;
    },
    ...options,
  });
};

const useChargerCreateAndUpdate = (params = {}, options = {}) => {
  const { id } = params;
  const apiStatement = id
    ? (payload) => httpService.post(`/charger/update/${id}`, payload)
    : (payload) => httpService.post(`/charger/create`, payload);

  return useMutation({
    mutationKey: ['CHARGER_CREATE_UPDATE', params],
    mutationFn: async (payload) => {
      const response = await apiStatement(payload);

      return response.data.data;
    },
    ...options,
  });
};

const useGetSerialNumber = (params = {}, options = {}) => {
  const { token, clientId } = params;

  return useQuery({
    queryKey: ['CREATE_STATION', clientId],
    queryFn: async () => {
      const response = await httpService.post(
        '/charger/get-serial-number',
        token,
        { clientId }
      );

      return response.data.data;
    },
    ...options,
  });
};

const useDeleteCharger = (params = {}, options = {}) => {
  const { operatorId } = params;

  return useMutation({
    mutationKey: ['DELETE_CHARGER', params],
    mutationFn: async ({id, opId}) => {
      const response = await httpService.post(`charger/delete/${id}`, {
        operatorId: operatorId || opId,
      });

      return response.data.data;
    },
    ...options,
  });
};

const useDeleteChargingStation = (operatorId, options = {}) => {
  return useMutation({
    mutationKey: ['deleteStation'],
    mutationFn: async ({ id }) => {
      const response = await httpService.post(`location/delete/${id}`, {
        operatorId: operatorId,
      });

      return response.data.data;
    },
    enabled: !!operatorId,
    ...options,
  });
};

const useChangeChargerAvailability = (options = {}) => {
  return useMutation({
    mutationKey: ['CHANGE_CHARGER_AVAILABILITY'],
    mutationFn: async (data) => {
      const response = await httpService.post(
        'charger/ocpp-change-availability',
        data
      );
      return response.data.data;
    },
    ...options,
  });
};

const useClearOcppCache = (options = {}) => {
  return useMutation({
    mutationKey: ['CLEAR_OCPP_CACHE'],
    mutationFn: async (data) => {
      const response = await httpService.post('charger/ocpp-clear-cache', data);
      return response.data.data;
    },
    ...options,
  });
};

const useSetOcppConfig = (options = {}) => {
  return useMutation({
    mutationKey: ['SET_OCPP_CONFIG'],
    mutationFn: async (data) => {
      const response = await httpService.post('charger/set-ocpp-config', data);
      return response.data.data;
    },
    ...options,
  });
};

const useTriggerOcppConfig = (options = {}) => {
  return useMutation({
    mutationKey: ['TRIGGER_OCPP_CONFIG'],
    mutationFn: async (data) => {
      const response = await httpService.post(
        'charger/get-ocpp-config-trigger',
        data
      );
      return response.data.data;
    },
    ...options,
  });
};

const useStopChargingModal = (options = {}) => {
  return useMutation({
    mutationKey: ['STOP_CHARGING_MODAL'],
    mutationFn: async (data) => {
      const response = await httpService.post(
        `charger-history/authority-stop`,
        data
      );
      return response.data.data;
    },
    ...options,
  });
};

const useStartCharging = (options = {}) => {
  return useMutation({
    mutationKey: ['START_CHARGING'],
    mutationFn: async (data) => {
      const response = await httpService.post(
        'charger-history/authority-start',
        data
      );
      return response.data.data;
    },
    ...options,
  });
};

const useGetChargerUtilization = () => {
  return useMutation({
    mutationKey: ['CHARGER_UTILIZATION', ],
    mutationFn: async ({id, startDate, endDate}) => {
      const response = await httpService.get(
        `charger/utilization-details/${id}?startDate=${startDate}&endDate=${endDate}`
      );
      return response.data.data;
    },
  });
};

const useGetChargerUptime = () => {
  return useMutation({
    mutationKey: ['CHARGER_UPTIME'],
    mutationFn: async ({id, startDate, endDate}) => {
      const response = await httpService.get(
        `charger/uptime-details/${id}?startDate=${startDate}&endDate=${endDate}`
      );
      return response.data.data;
    },
  });
};

// Start Diagnostic
const useStartDiagnostic = (options = {}) => {
  return useMutation({
    mutationKey: ['START_DIAGNOSTIC'],
    mutationFn: async (chargerId) => {
      const response = await httpService.get(`charger/start-diagnostic/${chargerId}`);
      return response.data.data;
    },
    ...options,
  });
};

// Get Diagnostics
const useGetDiagnostics = (chargerId, offset = 0, limit = 10, type = 'Diagnostic', options = {}) => {
  return useQuery({
    queryKey: ['GET_DIAGNOSTICS', chargerId, offset, limit, type],
    queryFn: async () => {
      const response = await httpService.get(
        `/charger/ocpp-request-list/${chargerId}?type=${type}&offset=${offset}&limit=${limit}`
      );
      return response.data.data;
    },
    enabled: !!chargerId,
    refetchInterval: 10000,
    ...options,
  });
};

const useBlockLocation = (options = {}) => {
  return useMutation({
    mutationKey: ['blockLocation'],
    mutationFn: async ({id, status}) => {
      const response = await httpService.post(`location/block-location/${id}`,{
        status: status,
      });
      return response.data.data;
    },
    ...options,
  });
};

const useBlockCharger = (options = {}) => {
  return useMutation({
    mutationKey: ['blockCharger'],
    mutationFn: async ({ id, status }) => {
      const response = await httpService.post(`charger/block/${id}`, { status });
      return response.data.data;
    },
    ...options,
  });
};

const useSetLocalList = (chargerId, options = {}) => {
  return useMutation({
    mutationKey: ['SET_LOCAL_LIST', chargerId],
    mutationFn: async (data) => {
      const response = await httpService.post(
        `charger/set-local-list/${chargerId}`,
        data
      );
      return response.data.data;
    },
    ...options,
  });
};

// Reboot Charger
const useRebootCharger = (options = {}) => {
  return useMutation({
    mutationKey: ['REBOOT_CHARGER'],
    mutationFn: async ({chargerId, operatorId}) => {
      const response = await httpService.post(`charger/reboot/${chargerId}`, {
        operatorId: operatorId,
      });
      return response.data.data;
    },
    ...options,
  });
};

// Get Status Notification
const useGetStatusNotification = (options = {}) => {
  return useMutation({
    mutationKey: ['GET_STATUS_NOTIFICATION'],
    mutationFn: async (chargerId) => {
      const response = await httpService.get(`charger/get-status-notification/${chargerId}`);
      return response.data.data;
    },
    ...options,
  });
};

// Firmware Update
const useUpdateFirmware = (options = {}) => {
  return useMutation({
    mutationKey: ['UPDATE_FIRMWARE'],
    mutationFn: async ({ chargerId, operatorId, firmwareFilePath }) => {
      const response = await httpService.post(
        `charger/update-firmware/${chargerId}`,
        {
          operatorId,
          firmwareFilePath,
        }
      );
      return response.data.data;
    },
    ...options,
  });
};

// Firmware History (reusing useGetDiagnostics with type='Firmware')
const useGetFirmwareHistory = (chargerId, offset = 0, limit = 10, options = {}) => {
  return useGetDiagnostics(chargerId, offset, limit, 'Firmware', options);
};

// Unlock Connector
const useUnlockConnector = (options = {}) => {
  return useMutation({
    mutationKey: ['UNLOCK_CONNECTOR'],
    mutationFn: async ({ chargerId, connectorId }) => {
      const response = await httpService.post('charger/unlock-connector', {
        chargerId,
        connectorId,
      });
      return response.data.data;
    },
    ...options,
  });
};

// Trigger Message
const useTriggerMessage = (options = {}) => {
  return useMutation({
    mutationKey: ['TRIGGER_MESSAGE'],
    mutationFn: async ({ chargerId, type }) => {
      const response = await httpService.post('charger/trigger-message', {
        chargerId,
        type,
      });
      return response.data.data;
    },
    ...options,
  });
};

export {
  useChargerCreateAndUpdate,
  useCreateLocation,
  useDeleteCharger,
  useDeleteChargingStation,
  useFetchChargingStations,
  useFetchSingleLocation,
  useGetSerialNumber,
  useGetChargersByLocation,
  useStopCharging,
  useGetSingleCharger,
  useChangeChargerAvailability,
  useClearOcppCache,
  useSetOcppConfig,
  useTriggerOcppConfig,

  useStopChargingModal,
  useStartCharging,
  useGetChargerUtilization,
  useGetChargerUptime,
  useStartDiagnostic,
  useGetDiagnostics,
  useBlockLocation,
  useBlockCharger,
  useSetLocalList,
  useRebootCharger,
  useGetStatusNotification,
  useUpdateFirmware,
  useGetFirmwareHistory,
  useUnlockConnector,
  useTriggerMessage,
};
